import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Mail, MapPin, Phone} from 'lucide-react';
// import { medSpa } from '../data/mockData';
// import { Service } from '../types/booking';
import {FloatingServiceSummary} from "../../components/floatingServiceSummary/FloatingServiceSummary";
import {ServiceCard} from "./components/ServiceCard";
import {Category, Service} from "../../types";
import {useBookService} from "../bookService/useBookService";
import {GenericSkeleton} from "../../components/skeleton/genericSkeleton";
import {ExistingServices} from "../bookService/components/existingServices";
import {CategoryAccordion} from "../../subframe";
import HairStudioPlaceHolderImg from "app/assets/images/hairStudioPlaceholder.jpg";
import {Avatar} from "../../components/avatar/avatar";


interface ServiceSelectionProps {
    onServiceSelect: (service: Service) => void;
    onBack: () => void;
}

export const ServiceSelection = ({
                                     onServiceSelect,
                                     onBack
                                 }: ServiceSelectionProps) => {


    const {
        shopProvidedServicesCategories,
        handleServiceClick,
        cart,
        isLoading,
        onRemoveMerchantService,
        navigateToStaff,
        navigateToTime,
        account,
        appointmentGroupToReschedule,
    } = useBookService();


    const [activeCategory, setActiveCategory] = useState('All');
    const [selectedCategoryId, setSelectedCategoryId] =
        useState<string>('All');
    const extractCategories = (services: Service[]): Category[] => {
        const map = new Map<string, Category>();
        map.set('All', {id: 'All', name: 'All'});
        for (const service of services) {
            const id = service.serviceCategory.id.toString();

            if (!map.has(id)) {
                map.set(id, {
                    id,
                    name: service.serviceCategory.name,
                });
            }
        }

        return Array.from(map.values());
    };

    const filterServicesByCategory = (
        services: Service[],
        categoryId: string
    ): Service[] => {
        if (categoryId === 'All') return services;

        return services.filter(
            s => s.serviceCategory.id.toString() === categoryId
        );
    };


// Derived data (memoized)
    const categories = useMemo(
        () => extractCategories(account.services || []),
        [account]
    );

    const visibleServices = useMemo(
        () =>
            filterServicesByCategory(account.services || [], selectedCategoryId),
        [account, selectedCategoryId]
    );

    // Stable handler reference
    const handleCategorySelect = useCallback(
        (categoryId: string) => {
            setSelectedCategoryId(categoryId);
        },
        []
    );

    const getShopDetails = useCallback(() => {
        const shop = account?.shops?.[0] || ({} as any)
        const address = `${shop.name}, ${shop?.addressLine1} ${shop?.addressLine2}`
        const phone = shop.phone
        const email = shop.email
        return {address, phone, email}
    }, [account])


    if (!account) return null
    if (isLoading) {
        return (
            <div className="my-4 mobile:pt-0 mobile:pb-0">
                <GenericSkeleton/>
            </div>
        );
    }



    // if(cart?.merchantServices?.length > 0) {
    //
    //    return <ExistingServices
    //         cart={cart}
    //         onRemoveMerchantService={onRemoveMerchantService}
    //         navigateToTime={navigateToTime}
    //     />
    // }

    return (
        <div className="min-h-screen w-full bg-slate-50 flex flex-col items-center overflow-x-hidden">
            {/* Header */}
            <div className="bg-white border-b border-slate-200 w-full mobile:hidden">
                <div className="px-6 py-4 max-w-4xl mx-auto w-full">
                </div>
            </div>

            {/* Business Header */}
            <div className="bg-white border-b border-slate-200 w-full">
                <div className="px-4 py-4 max-w-4xl mx-auto w-full">
                    <div className="flex items-start gap-4 mb-4 mobile:gap-3">

                        <div className="w-20 h-20 mobile:w-14 mobile:h-14 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0 flex items-center justify-center">


                            {account?.logo ?
                                <img
                                    src={account?.logo}
                                    alt={'placeholder logo'}
                                    className="w-full h-full object-contain bg-white"
                                />

                                :

                                <Avatar
                                    type={'character'}
                                    src={''}
                                    size={54}
                                    name={account.name}
                                />



                            }

                        </div>
                        <div className="min-w-0 flex-1">
                            <h1 className="text-2xl mobile:text-xl font-bold text-slate-900 break-words">{account?.name}</h1>
                            <p className="text-slate-600 text-sm mt-1 mb-2">{account?.description}</p>
                            <div className="flex flex-wrap items-center gap-2 mt-3">
                                <div className="flex items-center gap-2 bg-slate-100 px-3 py-1.5 rounded-full max-w-full min-w-0">
                                    <MapPin className="w-4 h-4 text-slate-500 flex-shrink-0"/>
                                    <span
                                        className="text-slate-700 text-sm font-medium">{getShopDetails().address}</span>
                                </div>
                                <div className="flex items-center gap-2 bg-slate-100 px-3 py-1.5 rounded-full max-w-full min-w-0">
                                    <Phone className="w-4 h-4 text-slate-500 flex-shrink-0"/>
                                    <span className="text-slate-700 text-sm font-medium">{getShopDetails().phone}</span>
                                </div>
                                <div className="flex items-center gap-2 bg-slate-100 px-3 py-1.5 rounded-full max-w-full min-w-0">
                                    <Mail className="w-4 h-4 text-slate-500 flex-shrink-0"/>
                                    <span className="text-slate-700 text-sm font-medium break-all">{getShopDetails().email}</span>
                                </div>
                            </div>
                        </div>
                    </div>


                    {

                       cart && cart?.merchantServices?.length > 0 ? (
                        <ExistingServices
                            cart={cart}
                            onRemoveMerchantService={onRemoveMerchantService}
                            navigateToTime={navigateToTime}
                        />
                    ) :

                            (     // Phones: one swipeable row so long category names aren't squeezed into narrow columns
                                <div className="flex gap-1 overflow-x-auto scrollbar-hide sm:grid sm:grid-cols-6 bg-slate-100 p-1 rounded-lg mb-4">
                            {categories.map((category) => (
                                <button
                                    key={category.id}
                                    onClick={() => {
                                        handleCategorySelect(category.id)
                                        setActiveCategory(category.id)
                                    }}
                                    className={`shrink-0 whitespace-nowrap sm:whitespace-normal py-1.5 px-3 sm:px-2 rounded-md text-xs font-medium transition-all text-center ${
                                        activeCategory === category.id
                                            ? 'bg-blue-600 text-white shadow-sm'
                                            : 'text-slate-600 hover:text-slate-900 hover:bg-white'
                                    }`}
                                >
                                    {category.name}
                                </button>
                            ))}

                        </div>
                            )
                    }




                </div>
            </div>


            {
               !cart?.merchantServices?.length  &&
                <div className="px-4 py-2 w-full max-w-4xl">
                <div className="space-y-2">
                    {visibleServices?.map((service: Service) => (
                        <ServiceCard
                            key={service.id}
                            service={service}
                            onBook={handleServiceClick}
                        />
                    ))}
                </div>

                {visibleServices?.length === 0 && (
                    <div className="text-center py-6 text-slate-500">
                        <div className="text-base font-medium mb-1">No services available</div>
                        <div className="text-sm">Try selecting a different category</div>
                    </div>
                )}
            </div>
            }

            {/* Floating Service Summary */}
            <FloatingServiceSummary
                selectedService={null}
                selectedAddonServices={[]}
                selectedPractitioner={null}
                onNext={() => {
                }}
                showNextButton={false}
            />
        </div>
    );
};
